import React from "react";
import { Button, Skeleton } from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Controller, useFormContext } from "react-hook-form";
import { IExlyFileUpload } from "./ExlyFileUpload.interfaces";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import { useExlyFileUpload } from "./ExlyFileUpload.utils";
import { RenderFilePreview } from "./module/RenderFilePreview/RenderFilePreview";
import { default_file_accept_types } from "./ExlyFileUpload.constants";
import styles from "./ExlyFileUpload.module.scss";

/**
 * @dev File upload config (filepath and bucket name) should be discussed with <@ayush> and
 * <@mayankdgupta> before using this common file uploader
 */

const ExlyFileUpload = (props: IExlyFileUpload) => {
    const {
        name,
        classes,
        registerOptions,
        onChange,
        CustomErrorMessageComponent,
        onClear,
        generateUploadKey,
        handleFileUploadStart,
        handleFileUploadEnd,
        accept = default_file_accept_types,
    } = props;

    const {
        getValues,
        control,
        formState: { errors },
        clearErrors,
    } = useFormContext();
    const error = name ? errors[name] : null;
    const showError = !!error;
    const { message: errorMessage } = error || {};

    const data = getValues();

    // Incase file is already uploaded, and we have to show the name pre-filled.
    // Example: Edit flow for already entered custom questions, in multiple user booking in checkout page.
    const alreadyUploadedFile = data?.[`${name}-file`];

    const {
        fileUploaded,
        isFileUploading,
        customErrorMessage,
        handleFileInputChange,
        handleClearFileInputChange,
    } = useExlyFileUpload({
        onChange,
        onClear,
        generateUploadKey,
        name: `${name}`,
        clearErrors,
        handleFileUploadStart,
        handleFileUploadEnd,
        alreadyUploadedFile,
        accept,
    });

    /**
     * Todo: @dev This will be updated once the file size increases,
     * we will need to also show the percentage of the upload one
     */
    if (isFileUploading) {
        return (
            <Skeleton variant="rectangular" className={styles.shimmerStyle} />
        );
    }

    return (
        <Controller
            name={`${name}`}
            control={control}
            rules={registerOptions}
            render={() => (
                <>
                    {fileUploaded.type ? (
                        <RenderFilePreview
                            fileUploaded={fileUploaded}
                            onClick={handleClearFileInputChange}
                        />
                    ) : (
                        <Button
                            variant="contained"
                            component="label"
                            startIcon={
                                <FileUploadOutlinedIcon
                                    className={styles.uploadIconStyle}
                                />
                            }
                            className={styles.uploadButtonStyle}
                        >
                            <div className={styles.uploadButtonText}>
                                Upload file
                            </div>
                            <input
                                type="file"
                                hidden
                                onChange={handleFileInputChange}
                                accept={accept.join(",")}
                            />
                        </Button>
                    )}
                    <ErrorMessage
                        showError={showError || Boolean(customErrorMessage)}
                        classes={classes}
                        CustomErrorMessageComponent={
                            CustomErrorMessageComponent
                        }
                        errorMessage={errorMessage || customErrorMessage}
                    />
                </>
            )}
        />
    );
};

export default ExlyFileUpload;
